import React, { useRef } from "react";
import { useReactToPrint } from "react-to-print";
import {
    Box,
    Typography,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Divider,
    Grid,
    Button,
    Paper,
    IconButton,
    Tooltip,
    Checkbox
} from "@mui/material";

import Close from '@mui/icons-material/Close';
import Printicon from "@mui/icons-material/Print";
import ResponsiveBox from "../../../Layout/ResponsiveScrollBox";

/**
 * Updated SalesOrderReport:
 * - Adds centered "Confirmation Order" header
 * - Confirmation No/Date boxed at right
 * - Full-width Remarks area
 * - Right-aligned summary card for totals (fixed alignment)
 * - Print button hidden in print media
 */
const DispatchPlanReport = ({ orderData, handleBackToSalesOrderDetails, totals }) => {

    const { clsPickList, PickListGroupItem, PickListItem } = orderData;
    const reportRef = useRef();

    const handlePrint = useReactToPrint({
        content: () => reportRef.current,
    });


    return (
        <>
            <Grid container justifyContent="space-between" alignItems="center" padding={0}>
                {/* Left-aligned: Edit Employee */}
                <Grid item>
                    <Typography variant="h4" sx={{ textAlign: "center", width: "100%", marginLeft: "10px", alignItems: "center" }}>
                        Print Despatch Plan Report
                    </Typography>
                </Grid>

                {/* Right-aligned: Buttons */}
                <Grid item>
                    <Grid container alignItems="center">
                        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                        {/* Save */}
                        <Grid item ml={1} mr={1}>
                            <Tooltip title="Add new orders">
                                <IconButton
                                    onClick={() => handlePrint()}
                                    sx={{ display: 'flex', alignItems: 'center' }}
                                // type="submit"
                                >
                                    <Printicon style={{ fontSize: "18px", color: "#009688" }} />
                                    <Typography variant="body2" sx={{ ml: 1 }}> Print</Typography>
                                </IconButton>
                            </Tooltip>
                        </Grid>

                        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                        <IconButton onClick={handleBackToSalesOrderDetails}>
                            <Close style={{ fontSize: "14px", color: "#2196F3" }} />
                        </IconButton>

                        <Divider orientation="vertical" flexItem />

                    </Grid>
                </Grid>
            </Grid>

            <Divider />
            <Box>

                <ResponsiveBox
                    customMaxHeight={{
                        xs: '60vh',  // Custom value for extra small devices
                        sm: '65vh',  // Custom value for small devices
                        md: '70vh',  // Custom value for medium devices
                        lg: '84vh',  // Custom value for large devices
                        xl: '89.6vh',  // Custom value for extra large devices
                    }}>

                    {/* Report content sized for A4 */}
                    <Paper
                        ref={reportRef}
                        elevation={0}
                        sx={{
                            p: 3,
                            bgcolor: "#fff",
                            width: "210mm",            // A4 width for print
                            minHeight: "297mm",
                            mx: "auto",
                            color: "#000",
                            mt: 2,
                            mb: 2

                        }}
                    >
                        {/* Company header */}
                        {/* borderBottom: "1px solid #000", */}
                        <Grid container alignItems="center" sx={{ pb: 1 }}>
                            {/* Left corner: Report Type */}
                            <Grid item xs={3} sx={{ textAlign: "left" }}>
                                <Box
                                    component="img"
                                    src="" // 👉 put your FSC/logo image path here
                                    alt="Company Logo"
                                    sx={{ width: 80, height: "auto" }}
                                />
                            </Grid>

                            {/* Center: Company Info */}
                            <Grid item xs={6} sx={{ textAlign: "center" }}>
                                <Typography variant="h6" fontWeight="bold">
                                    {clsPickList.ICompanyName}
                                </Typography>

                                <Typography variant="body2">
                                    {clsPickList?.ICompanyAddrLine1}
                                    {clsPickList?.ICompanyAddrLine2 ? `, ${clsPickList?.ICompanyAddrLine2}` : ""}
                                    {clsPickList?.ICompanyAddrLine3 ? `, ${clsPickList?.ICompanyAddrLine2}` : ""}
                                </Typography>

                                <Typography variant="body2">
                                    {clsPickList?.ICompanyAddrState}{" "}
                                    {clsPickList?.ICompanyAddrCountry
                                        ? ` ${clsPickList?.ICompanyAddrCountry}`
                                        : ""}
                                    {clsPickList?.ICompanyAddrEmailId
                                        ? ` EMAIL ID: ${clsPickList?.ICompanyAddrEmailId}`
                                        : ""}
                                </Typography>

                                <Typography variant="body2">
                                    GSTIN: {clsPickList?.ICompanyGSTNo}
                                </Typography>
                            </Grid>

                            {/* Right corner: Version / R1 */}
                            <Grid item xs={3} sx={{ textAlign: "right" }}>
                                <Typography variant="body2" fontWeight="bold">
                                    {/* {orderData?.OrderInvoices?.[0]?.SOrderStatus || "R1"} */}
                                    {/* {ReportStatus} */}
                                    Loading Plan
                                </Typography>
                            </Grid>
                        </Grid>



                        {/* Item Table */}
                        {/* Wrapper Table */}
                        <Table sx={{ width: "100%", borderCollapse: "collapse" }}>
                            <TableBody>


                                {/* Plan Details Section */}
                                <TableRow>
                                    <TableCell
                                        sx={{ border: "1px solid #000", p: "4px", color: "black" }}
                                        colSpan={2}
                                        valign="top"
                                    >
                                        <strong>Despatch Plan No</strong>: {clsPickList.DespatchPlanNo}
                                    </TableCell>
                                    <TableCell
                                        sx={{ border: "1px solid #000", p: "4px", color: "black" }}
                                        colSpan={2}
                                        valign="top"
                                    >
                                        <strong>Truck Number</strong>: {clsPickList.TruckNumber}
                                    </TableCell>
                                    <TableCell
                                        sx={{ border: "1px solid #000", p: "4px", color: "black" }}
                                        colSpan={2}
                                        valign="top"
                                    >
                                        <strong>Revision</strong>: {0}
                                    </TableCell>
                                    <TableCell
                                        sx={{ border: "1px solid #000", p: "4px", color: "black" }}
                                        colSpan={2}
                                        valign="top"
                                    >
                                        <strong>Date</strong>: {clsPickList.DespatchPlanDate}
                                    </TableCell>
                                </TableRow>


                                {/* Addresses Section */}
                                <TableRow>
                                    {/* Bill-To */}
                                    <TableCell
                                        sx={{ border: "1px solid #000", p: "4px", color: "black" }}
                                        colSpan={4.5}
                                        valign="top"
                                    >
                                        <b>Bill-To :</b><br />

                                        Parent: {PickListGroupItem[0]?._IBillToParentName || ''}<br />
                                        Bill to: {PickListGroupItem[0]?._IBillToName || ''}
                                    </TableCell>


                                    {/* Ship-To */}
                                    <TableCell
                                        sx={{ border: "1px solid #000", p: "4px", color: "black" }}
                                        colSpan={4}
                                        valign="top"
                                    >
                                        <b>Ship-To :</b><br />
                                        Ship to: {clsPickList.IShipToName || ''}
                                    </TableCell>

                                </TableRow>




                                {/* Order Details Section */}
                                <TableRow>
                                    <TableCell
                                        sx={{ border: "1px solid #000", p: "4px", color: "black" }}
                                        colSpan={3}
                                        valign="top"
                                    >
                                        <strong>Order No</strong>: {PickListGroupItem[0]?._ConfirmationOrderNo}
                                    </TableCell>
                                    <TableCell
                                        sx={{ border: "1px solid #000", p: "4px", color: "black" }}
                                        colSpan={3}
                                        valign="top"
                                    >
                                        <strong>Quality</strong>: {PickListGroupItem[0]?._Variety}
                                    </TableCell>
                                    <TableCell
                                        sx={{ border: "1px solid #000", p: "4px", color: "black" }}
                                        colSpan={3}
                                        valign="top"
                                    >
                                        <strong>GSM</strong>: {PickListGroupItem[0]?._GSM}
                                    </TableCell>

                                </TableRow>



                                {/* Item Table Header */}
                                <TableRow sx={{ bgcolor: "#eaeaea", color: "black" }}>
                                    <TableCell sx={{ border: "1px solid #000", textAlign: "center", p: "2px 4px", fontWeight: "bold", width: "40px", color: "black" }}>
                                        S. No
                                    </TableCell>
                                    <TableCell sx={{ border: "1px solid #000", p: "2px 4px", fontWeight: "bold", width: "50px", color: "black" }}>
                                        Size (Inch) Location
                                    </TableCell>
                                    <TableCell sx={{ border: "1px solid #000", textAlign: "center", p: "2px 4px", fontWeight: "bold", width: "50px", color: "black" }}>
                                        Ream Wt.
                                    </TableCell>
                                    <TableCell sx={{ border: "1px solid #000", textAlign: "center", p: "2px 4px", fontWeight: "bold", width: "50px", color: "black" }}>
                                        Reams per Bundle
                                    </TableCell>
                                    <TableCell sx={{ border: "1px solid #000", p: "2px 4px", fontWeight: "bold", width: "150px", color: "black" }}>
                                        Bundle Weight
                                    </TableCell>
                                    <TableCell sx={{ border: "1px solid #000", textAlign: "center", p: "2px 4px", fontWeight: "bold", width: "70px", color: "black" }}>
                                        No of Reels /Bundles
                                    </TableCell>
                                    <TableCell sx={{ border: "1px solid #000", textAlign: "center", p: "2px 4px", fontWeight: "bold", width: "80px", color: "black" }}>
                                        Weight (kg)
                                    </TableCell>
                                    <TableCell sx={{ border: "1px solid #000", textAlign: "center", p: "2px 4px", fontWeight: "bold", width: "50px", color: "black" }}>
                                        Picked?
                                    </TableCell>

                                </TableRow>

                                {PickListGroupItem?.map((item, index) => (
                                    <TableRow key={index}>
                                        <TableCell
                                            sx={{ border: "1px solid #000", textAlign: "center", p: "2px 4px", lineHeight: 1.2, fontSize: "12px", color: "black" }}
                                        >
                                            {index + 1}
                                        </TableCell>
                                        <TableCell sx={{ border: "1px solid #000", p: "2px 4px", fontSize: "12px", color: "black" }}>{item._Width}X{item._Length}</TableCell>
                                        <TableCell sx={{ border: "1px solid #000", p: "2px 4px", fontSize: "12px", color: "black" }}>{item._ReamWeight}</TableCell>
                                        <TableCell sx={{ border: "1px solid #000", p: "2px 4px", fontSize: "12px", color: "black", textAlign: "center" }}>{item._ReamsPerBundle}</TableCell>
                                        <TableCell sx={{ border: "1px solid #000", p: "2px 4px", fontSize: "12px", color: "black", textAlign: "center" }}>
                                            {item._Qty}
                                        </TableCell>
                                        <TableCell
                                            sx={{ border: "1px solid #000", textAlign: "right", p: "2px 4px", fontSize: "12px", color: "black" }}
                                        >
                                           {item._NoOfBundlesReels}
                                        </TableCell>

                                        <TableCell sx={{ border: "1px solid #000", textAlign: "right", p: "2px 4px", fontSize: "12px", color: "black" }}>
                                            {item._Qty}
                                        </TableCell>
                                        <TableCell sx={{ border: "1px solid #000", p: "2px 4px", fontSize: "12px", textAlign: "center" }}> <input type="checkbox" /></TableCell>

                                    </TableRow>
                                ))}


                                {/* Totals Section */}
                                <TableRow>
                                    <TableCell
                                        sx={{ border: "1px solid #000", p: "4px", color: "black" }}
                                        colSpan={3}
                                        valign="top"
                                    >
                                        {/* <strong>Order No</strong>: {PickListGroupItem[0]._ConfirmationOrderNo} */}
                                    </TableCell>
                                    <TableCell
                                        sx={{ border: "1px solid #000", p: "4px", color: "black" }}
                                        colSpan={3}
                                        valign="top"
                                    >
                                        {/* <strong>Quality</strong>: {PickListGroupItem[0]._Variety} */}
                                    </TableCell>
                                    <TableCell
                                        sx={{ border: "1px solid #000", p: "4px", color: "black" }}
                                        colSpan={3}
                                        valign="top"
                                    >
                                        <strong>Sub Total (Kg)</strong>: {PickListItem?.reduce(
                                            (sum, item) => sum + (Number(item?._Qty) || 0),
                                            0
                                        )}<br />
                                        <strong>Grand Total (Kg)</strong>:

                                        {PickListItem?.reduce(
                                            (sum, item) => sum + (Number(item?._Qty) || 0),
                                            0
                                        )}
                                    </TableCell>
                                </TableRow>


                                {/* footer */}
                                <TableRow>
                                    <TableCell
                                        sx={{ border: "1px solid #000", p: "4px", color: "black" }}
                                        colSpan={3}
                                        valign="top"
                                    >
                                        <Typography>Prepared By</Typography>
                                    </TableCell>
                                    <TableCell
                                        sx={{ border: "1px solid #000", p: "4px", color: "black" }}
                                        colSpan={3}
                                        valign="top"
                                    >
                                        <Typography>Checked By</Typography>
                                    </TableCell>
                                    <TableCell
                                        sx={{ border: "1px solid #000", p: "4px", color: "black" }}
                                        colSpan={3}
                                        valign="top"
                                    >
                                        <strong>{clsPickList.IShipToName}</strong><br />
                                        <strong>Authorized Signatories</strong>

                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>

                    </Paper>
                </ResponsiveBox>
            </Box>

        </>
    );
};

export default DispatchPlanReport;

